<template>
  <WdsButtonGhost
    v-if="!showSlugInput"
    primary
    data-test="slug-input-button"
    @click.prevent="showSlugInput = true"
  >
    <WdsStack gap-sm align-center>
      <WdsTooltip>
        <template #tooltip-content>
          {{ tooltipText }}
        </template>
        <WdsIcon name="info" :size="16" />
      </WdsTooltip>

      {{ buttonText }}
      <WdsIcon class="tw-ml-2" color="#0E0D12" name="edit" weight="thick" :size="20" />
    </WdsStack>
  </WdsButtonGhost>

  <WdsInput
    v-if="showSlugInput"
    v-model="slugValue"
    data-test="custom-field-slug"
    :disable="disabled"
    :label="$t('Unique key')"
    :error="error"
  >
    <template #label>
      <WdsStack gap-sm align-center>
        <WdsTooltip :text="tooltipText">
          <WdsIcon name="info" :size="16" />
        </WdsTooltip>
        <span>{{ $t('Unique key') }}</span>
      </WdsStack>
    </template>
  </WdsInput>
  <WdsAlert v-if="showSlugInput && props.modelValue && isUpdated" warning class="mt-5">
    {{ $t('Updating unique key might break any existing integrations. Edit with caution.') }}
  </WdsAlert>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Ref, ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
  WdsTooltip,
  WdsStack,
  WdsAlert,
} from '@worksome/wds-components'
import { useVModel } from '@vueuse/core'
import { $t } from '@/composables/translation'

interface Props {
  modelValue?: string
  disabled?: boolean
  isUpdated?: boolean
  error?: string
}

const props = withDefaults(defineProps<Props>(), {
  isUpdated: false,
  disabled: false,
})

const emit = defineEmits(['update:modelValue'])

const showSlugInput: Ref<boolean> = ref(false)

const buttonText: ComputedRef<string> = computed(() =>
  props.modelValue
    ? $t('Unique key: :slug', { slug: props.modelValue })
    : $t('Customize unique key'),
)

const tooltipText: Ref<string> = ref(
  $t(
    "Enter a short, easy-to-use key for identification useful in integrations and data exports. We will automatically generate a value from the field name, but you can customize with your own. Use only lowercase letters, numbers, and hyphens. Example: 'my-custom-field'.",
  ),
)

const slugValue = useVModel(props, 'modelValue', emit)
</script>
